import React from 'react';
import { cn } from "@/lib/utils";

// Base Skeleton with shimmer effect
const Skeleton = ({ className, ...props }) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-md bg-[#E8E4DF]",
      "before:absolute before:inset-0 before:-translate-x-full",
      "before:animate-[shimmer_1.5s_infinite]",
      "before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent",
      className
    )}
    {...props}
  />
);

// Text skeleton - single line
const SkeletonText = ({ className, width = "100%" }) => (
  <Skeleton 
    className={cn("h-4 rounded", className)} 
    style={{ width }} 
  />
);

// Title skeleton
const SkeletonTitle = ({ className, size = "lg" }) => {
  const heights = {
    sm: "h-6",
    md: "h-8",
    lg: "h-10",
    xl: "h-12",
    "2xl": "h-16"
  };
  return (
    <Skeleton className={cn(heights[size], "rounded-lg w-3/4", className)} />
  );
};

// Paragraph skeleton - multiple lines
const SkeletonParagraph = ({ lines = 3, className }) => (
  <div className={cn("space-y-2", className)}>
    {Array.from({ length: lines }).map((_, i) => (
      <SkeletonText 
        key={i} 
        width={i === lines - 1 ? "60%" : "100%"} 
      />
    ))}
  </div>
);

// Image skeleton with aspect ratio
const SkeletonImage = ({ className, aspectRatio = "16/9" }) => (
  <Skeleton 
    className={cn("w-full rounded-2xl", className)} 
    style={{ aspectRatio }}
  />
);

// Avatar/Circle skeleton
const SkeletonAvatar = ({ size = "md", className }) => {
  const sizes = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
    xl: "w-20 h-20"
  };
  return (
    <Skeleton className={cn(sizes[size], "rounded-full", className)} />
  );
};

// Button skeleton
const SkeletonButton = ({ className, size = "md" }) => {
  const sizes = {
    sm: "h-8 w-20",
    md: "h-10 w-28",
    lg: "h-12 w-36",
    xl: "h-14 w-44"
  };
  return (
    <Skeleton className={cn(sizes[size], "rounded-full", className)} />
  );
};

// Card skeleton
const SkeletonCard = ({ className, hasImage = true, hasAvatar = false }) => (
  <div className={cn("bg-white rounded-3xl p-6 space-y-4", className)}>
    {hasImage && <SkeletonImage aspectRatio="16/10" />}
    <div className="space-y-3">
      {hasAvatar && (
        <div className="flex items-center gap-3">
          <SkeletonAvatar size="sm" />
          <div className="space-y-1 flex-1">
            <SkeletonText width="40%" />
            <SkeletonText width="25%" className="h-3" />
          </div>
        </div>
      )}
      <SkeletonTitle size="md" />
      <SkeletonParagraph lines={2} />
    </div>
  </div>
);

// Hero section skeleton
const SkeletonHero = ({ className }) => (
  <div className={cn("relative min-h-[70vh] rounded-3xl overflow-hidden", className)}>
    <Skeleton className="absolute inset-0 rounded-3xl" />
    <div className="absolute bottom-16 left-8 lg:left-16 space-y-4 z-10">
      <SkeletonText width="120px" className="h-3" />
      <SkeletonTitle size="2xl" className="w-[300px] lg:w-[500px]" />
      <SkeletonTitle size="xl" className="w-[200px] lg:w-[350px]" />
      <SkeletonParagraph lines={2} className="w-[250px] lg:w-[400px]" />
    </div>
  </div>
);

// Stats card skeleton
const SkeletonStats = ({ className }) => (
  <div className={cn("text-center p-8 rounded-3xl bg-white space-y-4", className)}>
    <Skeleton className="w-16 h-16 rounded-2xl mx-auto" />
    <SkeletonTitle size="xl" className="w-20 mx-auto" />
    <SkeletonText width="60px" className="mx-auto h-3" />
  </div>
);

// Contact card skeleton
const SkeletonContactCard = ({ className }) => (
  <div className={cn("bg-white/90 rounded-3xl p-8 space-y-4", className)}>
    <Skeleton className="w-16 h-16 rounded-2xl" />
    <SkeletonText width="40%" className="h-3" />
    <SkeletonTitle size="md" className="w-1/2" />
    <SkeletonParagraph lines={2} />
    <SkeletonText width="50%" className="h-4" />
  </div>
);

// Course pricing skeleton
const SkeletonPricing = ({ className }) => (
  <div className={cn("bg-white rounded-3xl p-8 space-y-6", className)}>
    <div className="space-y-2">
      <SkeletonText width="80px" className="h-3" />
      <SkeletonTitle size="xl" />
    </div>
    <Skeleton className="h-px w-full" />
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="w-5 h-5 rounded-full" />
          <SkeletonText width="70%" />
        </div>
      ))}
    </div>
    <SkeletonButton size="xl" className="w-full" />
    <SkeletonButton size="lg" className="w-full" />
  </div>
);

// FAQ item skeleton
const SkeletonFaqItem = ({ className }) => (
  <div className={cn("bg-[#FAF6F1] rounded-2xl p-6", className)}>
    <div className="flex items-center justify-between">
      <SkeletonText width="70%" className="h-5" />
      <Skeleton className="w-5 h-5 rounded" />
    </div>
  </div>
);

// Form skeleton
const SkeletonForm = ({ className }) => (
  <div className={cn("bg-white/90 rounded-3xl p-8 lg:p-10 space-y-5", className)}>
    <div className="flex items-center gap-3 mb-4">
      <Skeleton className="w-10 h-10 rounded-xl" />
      <SkeletonTitle size="md" className="w-48" />
    </div>
    <SkeletonText width="60%" className="mb-6" />
    
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <SkeletonText width="50px" className="h-3" />
        <Skeleton className="h-12 rounded-xl" />
      </div>
      <div className="space-y-2">
        <SkeletonText width="40px" className="h-3" />
        <Skeleton className="h-12 rounded-xl" />
      </div>
    </div>
    
    <div className="space-y-2">
      <SkeletonText width="50px" className="h-3" />
      <Skeleton className="h-12 rounded-xl" />
    </div>
    
    <div className="space-y-2">
      <SkeletonText width="60px" className="h-3" />
      <Skeleton className="h-28 rounded-xl" />
    </div>
    
    <SkeletonButton size="xl" className="w-full h-14" />
  </div>
);

// Info card skeleton (for schedule info)
const SkeletonInfoCard = ({ className }) => (
  <div className={cn("bg-white/80 rounded-2xl p-6", className)}>
    <div className="flex items-start gap-5">
      <Skeleton className="w-14 h-14 rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <SkeletonTitle size="sm" className="w-32" />
        <SkeletonText width="60%" />
        <SkeletonText width="40%" className="h-3" />
      </div>
    </div>
  </div>
);

// Timeline card skeleton
const SkeletonTimelineCard = ({ className, isLeft = true }) => (
  <div className={cn(
    "bg-white/20 backdrop-blur-sm rounded-2xl p-6 lg:p-8",
    className
  )}>
    <div className={cn("space-y-3", isLeft ? "text-right" : "text-left")}>
      <Skeleton className={cn("h-6 w-16 rounded-full", isLeft ? "ml-auto" : "")} />
      <SkeletonTitle size="lg" className={cn("w-48", isLeft ? "ml-auto" : "")} />
      <SkeletonParagraph lines={2} />
    </div>
  </div>
);

// Section skeleton - generic wrapper
const SkeletonSection = ({ className, children }) => (
  <div className={cn("py-4 px-3", className)}>
    <div className="window-content bg-white rounded-3xl py-10 lg:py-14 px-8">
      {children}
    </div>
  </div>
);

export {
  Skeleton,
  SkeletonText,
  SkeletonTitle,
  SkeletonParagraph,
  SkeletonImage,
  SkeletonAvatar,
  SkeletonButton,
  SkeletonCard,
  SkeletonHero,
  SkeletonStats,
  SkeletonContactCard,
  SkeletonPricing,
  SkeletonFaqItem,
  SkeletonForm,
  SkeletonInfoCard,
  SkeletonTimelineCard,
  SkeletonSection
};
